/**
 * CopilotChatContext - Context for the copilot chat
 */

import { createContext } from 'react';
import { IMessage, IConfigByCountry } from 'src/lib/schemas';

// Types matching backend OnboardingState
export interface CopilotLocation {
  id?: string;
  name: string;
  address: {
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
    lat?: number | string;
    long?: number | string;
    placeId?: string;
  };
  phone_number?: string;
  main?: boolean;
}

export interface CopilotServiceArea {
  id?: string;
  name: string;
  type?: 'radius' | 'geographic';
  address?: {
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
    lat?: number | string;
    long?: number | string;
    placeId?: string;
  } | null;
  radius?: number | null;
  geographic?: {
    entityType?: 'neighborhood' | 'city' | 'county' | 'state' | 'country';
    entityId?: string | null;
    entityName?: string;
    country?: string;
    state?: string | null;
    displayName?: string;
    lat?: number | null;
    lng?: number | null;
  } | null;
}

export interface CopilotCatalog {
  catalogId: string;
  id?: string;
  name: string;
  description?: string;
  price?: number;
  currency?: string;
  imageUrl?: string;
  image?: string;
  type?: string;
  kind?: string;
}

export interface CopilotCustomerProfile {
  _id: string;
  name: string;
  description: string;
  demographics?: Record<string, unknown>;
  interests?: string[];
  painPoints?: string[];
  customer_rules?: any[];
  customer_tones?: string[];
}

// Generated logo from AI
export interface GeneratedLogo {
  id: string;
  url: string;
  style: string;
}

export interface OnboardingState {
  currentStep: string;
  completedSteps: string[];
  websiteUrl?: string;
  businessName?: string;
  businessDescription?: string;
  industry?: string;
  phoneNumber?: string;
  logoUrl?: string;
  logoSquareUrl?: string;
  generatedLogos?: GeneratedLogo[];
  catalogs: CopilotCatalog[];
  selectedCatalogId?: string;
  channel?: 'online' | 'instore' | 'both';
  locations: CopilotLocation[];
  serviceAreas: CopilotServiceArea[];
  customerProfiles: CopilotCustomerProfile[];
  campaignId?: string;
  conversationId?: string;
}

export interface UIComponent {
  type: string;
  props: Record<string, unknown>;
}

// Prefill data for location modal
export interface LocationModalPrefill {
  name?: string;
  address?: string;
}

// Prefill data for product modal
export interface ProductModalPrefill {
  name?: string;
  description?: string;
  url?: string;
  type?: 'product' | 'service';
}

// Prefill data for service area modal
export interface ServiceAreaModalPrefill {
  type?: 'radius' | 'geographic';
  name?: string; // Area name (e.g., "30 miles from Buenos Aires")
  // Radius type fields
  address?: string;
  radius?: number;
  // Geographic type fields
  geographic?: {
    entityType: 'neighborhood' | 'city' | 'county' | 'state' | 'country';
    entityName: string;
    displayName?: string;
  };
}

// Prefill data for customer profile modal
export interface CustomerProfileModalPrefill {
  name?: string;
  description?: string;
  demographics?: {
    ageRange?: string;
    gender?: string;
    income?: string;
  };
  interests?: string[];
  painPoints?: string[];
  // New fields for audience targeting (from create-bundle endpoint)
  customer_rules?: any[];
  customer_tones?: any[];
  customer_keywords?: string[];
}

export interface CopilotChatContextType {
  // Connection state
  isConnected: boolean;
  isAssistantWriting: boolean;
  shouldShowTypingIndicator: boolean;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  isFunctionCallInProgress: boolean;
  isWaitingForAssistant: boolean;

  // Messages
  messages: IMessage[];

  // Actions
  sendMessage: (text: string, role?: string, hidden?: boolean, skipScroll?: boolean) => void;
  resetChat: () => void;
  retryLastMessage: () => void;
  lastFailedMessage: string | null;

  // Copilot UI state
  isCopilotOpen: boolean;
  openCopilot: (block?: boolean) => void;
  closeCopilot: () => void;
  isScreenBlocked: boolean;

  // Legacy compatibility
  countryConfig: IConfigByCountry | null;
  assistantAlias: string;

  // Conversation state
  conversationId: string | null;
  onboardingState: OnboardingState | null;

  // Modal state
  locationModalOpen: boolean;
  serviceAreaModalOpen: boolean;
  productModalOpen: boolean;
  customerProfileModalOpen: boolean;
  setLocationModalOpen: (open: boolean) => void;
  setServiceAreaModalOpen: (open: boolean) => void;
  setProductModalOpen: (open: boolean) => void;
  setCustomerProfileModalOpen: (open: boolean) => void;

  // Modal prefill data
  locationModalPrefill: LocationModalPrefill | null;
  clearLocationModalPrefill: () => void;
  serviceAreaModalPrefill: ServiceAreaModalPrefill | null;
  clearServiceAreaModalPrefill: () => void;
  productModalPrefill: ProductModalPrefill | null;
  clearProductModalPrefill: () => void;
  customerProfileModalPrefill: CustomerProfileModalPrefill | null;
  clearCustomerProfileModalPrefill: () => void;

  // Chat data
  currentLocations: CopilotLocation[];
  currentServiceAreas: CopilotServiceArea[];
  currentCatalogs: CopilotCatalog[];
  currentCustomerProfiles: CopilotCustomerProfile[];
  selectedChannel: string | null;

  // Data setters
  addLocation: (location: CopilotLocation) => void;
  updateLocation: (id: string, location: CopilotLocation) => void;
  addServiceArea: (serviceArea: CopilotServiceArea) => void;
  updateServiceArea: (id: string, serviceArea: CopilotServiceArea) => void;
  addCatalog: (catalog: CopilotCatalog) => void;
  addCustomerProfile: (profile: CopilotCustomerProfile) => void;
  updateCustomerProfile: (id: string, profile: CopilotCustomerProfile) => void;
  removeLocation: (id: string) => void;
  removeServiceArea: (id: string) => void;
  removeCatalog: (id: string) => void;
  removeCustomerProfile: (id: string) => void;
  setSelectedCatalogId: (id: string | null) => void;

  // Editing state (shared between Message.tsx and ChatWithModals)
  editingLocation: CopilotLocation | null;
  setEditingLocation: (location: CopilotLocation | null) => void;
  editingServiceArea: CopilotServiceArea | null;
  setEditingServiceArea: (serviceArea: CopilotServiceArea | null) => void;
  editingCustomerProfile: CopilotCustomerProfile | null;
  setEditingCustomerProfile: (profile: CopilotCustomerProfile | null) => void;

  // Domain selection
  selectDomain: (domain: string) => void;
}

export const CopilotChatContext = createContext<CopilotChatContextType>(
  {} as CopilotChatContextType
);
